/**
 * Famille & vacances scolaires
 *
 * Deux choses y vivent : le code d'invitation à partager avec le conjoint — sans
 * lui, toute la charge mentale reste sur une seule personne — et la zone de
 * vacances scolaires, avec le calendrier officiel en clair et la date de sa
 * dernière mise à jour.
 *
 * La règle « cantine et garderie au milieu de chaque période de vacances » est
 * montrée, pas expliquée : l'utilisateur doit la comprendre en voyant les dates.
 */

import { useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';

import { aujourdhuiParis, enClair, milieuDe } from '@foyer/noyau';

import { Champ, Chargement, Erreur, TRAIT } from '../composants/ui.js';
import { useAppli, useRessource } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';

export function Famille() {
  const { rafraichis, utilisateur, deconnecte } = useAppli();
  const aujourdhui = aujourdhuiParis();
  const foyer = useRessource(() => donnees.foyer());
  const vacances = useRessource(() => donnees.vacances());
  const zones = useRessource(() => donnees.zones());
  const ecole = useRessource(() => donnees.simuleEcole());

  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [copie, setCopie] = useState(false);

  if (foyer.chargement && !foyer.donnee) return <Chargement quoi="Chargement du foyer" />;
  if (foyer.erreur && !foyer.donnee) return <Erreur message={foyer.erreur} />;
  const famille = foyer.donnee?.famille;
  if (!famille) return <Erreur message="Foyer introuvable" />;

  const estParent = utilisateur?.role === 'parent';

  async function synchronise() {
    setOccupe(true);
    setMessage(null);
    try {
      const r = await donnees.synchroniseVacances();
      setMessage(`${r.periodes} périodes en cache.`);
      rafraichis();
      vacances.recharge();
      ecole.recharge();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  async function changeZone(zone: string) {
    setOccupe(true);
    try {
      await donnees.majFoyer({ zoneVacances: zone });
      rafraichis();
      foyer.recharge();
      vacances.recharge();
    } finally {
      setOccupe(false);
    }
  }

  async function nouveauCode() {
    setOccupe(true);
    try {
      await donnees.nouveauCode();
      rafraichis();
      foyer.recharge();
      setMessage('Nouveau code. L’ancien ne fonctionne plus.');
    } finally {
      setOccupe(false);
    }
  }

  async function activeEcole() {
    setOccupe(true);
    try {
      const r = await donnees.genereEcole();
      setMessage(`${r.creees.length} échéance(s) d’école programmée(s).`);
      rafraichis();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>
          Famille
        </h1>
      </div>

      {message && <div className="succes">{message}</div>}

      {/* ── Membres et invitation ─────────────────────────────────────────── */}
      <div className="carte">
        <h3>{famille.nom}</h3>
        <div className="sourdine" style={{ marginBottom: 10 }}>
          {(foyer.donnee?.membres ?? []).map((m) => `${m.prenom} (${m.role})`).join(' · ')}
        </div>

        <Champ
          etiquette="Code d’invitation"
          aide="À donner au conjoint pour qu’il rejoigne le foyer. Tant qu’il est dehors, toute la charge reste sur une seule personne."
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              readOnly
              value={famille.codeInvitation}
              style={{ fontFamily: 'var(--police-mono)', letterSpacing: '0.1em' }}
            />
            <button
              type="button"
              className="bouton icone"
              aria-label="Copier le code"
              onClick={() => {
                void navigator.clipboard?.writeText(famille.codeInvitation);
                setCopie(true);
                setTimeout(() => setCopie(false), 1800);
              }}
            >
              <Copy strokeWidth={TRAIT} size={18} aria-hidden />
            </button>
          </div>
          {copie && <div className="aide">Copié.</div>}
        </Champ>

        {estParent && (
          <button type="button" className="bouton texte" onClick={() => void nouveauCode()} disabled={occupe}>
            Régénérer le code
          </button>
        )}
      </div>

      {/* ── Zone et calendrier scolaire ───────────────────────────────────── */}
      <div className="carte">
        <h3>Vacances scolaires</h3>

        <Champ etiquette="Zone">
          <select
            value={famille.zoneVacances}
            disabled={!estParent || occupe}
            onChange={(e) => void changeZone(e.target.value)}
          >
            {(zones.donnee?.zones ?? [famille.zoneVacances]).map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </Champ>

        <div className="entre-deux">
          <span className="sourdine">
            {vacances.donnee?.etat?.dernierSucces
              ? `Calendrier à jour au ${enClair(vacances.donnee.etat.dernierSucces.slice(0, 10))}`
              : 'Calendrier jamais récupéré.'}
          </span>
          {estParent && (
            <button type="button" className="bouton secondaire" onClick={() => void synchronise()} disabled={occupe}>
              <RefreshCw strokeWidth={TRAIT} size={16} aria-hidden />
              {occupe ? 'Récupération…' : 'Mettre à jour'}
            </button>
          )}
        </div>

        {(vacances.donnee?.periodes.length ?? 0) === 0 ? (
          <div className="note" style={{ marginTop: 10 }}>
            Aucune période en cache. Sans le calendrier officiel, les rappels de cantine et de garderie
            ne peuvent pas être placés — l’application préfère ne rien programmer plutôt que d’inventer
            une date.
          </div>
        ) : (
          <table className="tableau">
            <thead>
              <tr>
                <th>Période</th>
                <th>Dates</th>
                <th>Cantine</th>
              </tr>
            </thead>
            <tbody>
              {vacances.donnee!.periodes
                .filter((p) => p.fin >= aujourdhui)
                .map((p) => (
                  <tr key={`${p.description}-${p.debut}`}>
                    <td>{p.description}</td>
                    <td>
                      {enClair(p.debut)} → {enClair(p.fin)}
                    </td>
                    <td>{enClair(milieuDe(p))}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}

        <p className="sourdine" style={{ marginTop: 8 }}>
          La colonne « Cantine » montre où tombe le rappel de paiement : au milieu de chaque période,
          quand la facture est arrivée et qu’on a le temps de s’en occuper.
        </p>

        {estParent && ecole.donnee && (
          <>
            <div className="actions">
              <button type="button" className="bouton secondaire" onClick={() => void activeEcole()} disabled={occupe}>
                Activer les rappels d’école
              </button>
            </div>
            <p className="sourdine">
              {ecole.donnee.regles.map((r) => r.titre).join(' · ')}
            </p>
          </>
        )}
      </div>

      {/* ── Compte ────────────────────────────────────────────────────────── */}
      <div className="carte">
        <h3>Mon compte</h3>
        <div className="sourdine">
          {utilisateur?.prenom} · {utilisateur?.email} · {utilisateur?.role}
        </div>
        <div className="actions">
          <a className="bouton secondaire" href="/api/export" download>
            Exporter mes données
          </a>
          <button type="button" className="bouton secondaire" onClick={() => void deconnecte()}>
            Se déconnecter
          </button>
        </div>
        <p className="sourdine">
          L’export contient tout ce que l’application sait de votre foyer, en JSON. C’est votre
          serveur, ce sont vos données.
        </p>
      </div>
    </>
  );
}
